import { useCallback,useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchRoleUserGroup } from 'common/services/api/RolesUserGroup/RoleUserGroup.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { RoleUserGroupCreateContent } from './RoleUserGroupCreateDrawerContent';
import { RoleUserGroupDeleteDialogContent } from './RoleUserGroupDeleteDialogContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './roleUserGroupSlice';

export const RoleUserGroupTab = () => {
    const typeName = 'Role';

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO ]);
    const [sortModel, setSortModel] = usePalmSortModel();
    const [processing, setProcessing] = useState<boolean>(false);
    const { suiteId = '', tenantId = '', usergroupId = '' } = useParams();

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId, userGroupId: usergroupId },
        fetchRoleUserGroup,
        setProcessing
    );

    const onGetRowLink = useCallback((roleId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Roles}/${roleId}/${Routes.UserGroups}`;
    }, []);

    const actionDefinitions = [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'Role ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Role Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={RoleUserGroupCreateContent}
        deleteFunction={RoleUserGroupDeleteDialogContent}
        title={typeName}
        isCreateButtonDisabled={shouldDisableButtons}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        filter={filterProp}
        processing={processing}
        operationName={Operations.Link}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
};
